// A reply as it streams: what each frame of the transcript verb's answer
// does to the turn being written (impire-hq 02-DESIGN/19-chat/
// 01-the-chat-solution.md § the verbs). Pure, so that the thread's behaviour
// under frames is tested without a connection: the first frame names the
// thread and how many older turns were left out, text appends, a progress
// frame means the model is thinking, the terminator records the outcome, an
// error terminator records the refusal's code — and an escalation's id — and
// keeps what arrived.

import { guard } from "@impire-io/sdk";

import { outcomeOf, resendBody, type HeaderReader, type Outcome, type SendBody } from "./wire.ts";

/** Event is one thing the stream yielded, as the relay module reports it. */
export type Event =
  | { kind: "request"; request: string }
  | { kind: "thread"; thread: string; left_out: number }
  | { kind: "text"; text: string }
  | { kind: "progress" }
  | { kind: "done"; headers: HeaderReader | undefined }
  | { kind: "error"; code: string; message: string; escalation?: string; thread?: string }
  | { kind: "lost" };

/** Failure is how a reply ended when it did not end done. */
export interface Failure {
  code: string;
  message: string;
  /** The crossing's id, on a `guard-escalated` refusal that carried it. */
  escalation?: string;
}

/** Reply is the turn being written. */
export interface Reply {
  text: string;
  /** The request id the instance minted, for cancel; empty until the first frame. */
  request: string;
  /** Whether the last frame was a progress frame: the model is thinking rather than writing. */
  thinking: boolean;
  /** Whether a frame has arrived at all. */
  started: boolean;
  /** The thread's id, as the first frame named it — minted there for a new thread; empty until then. */
  thread: string;
  /** How many older turns the assistant did not send. */
  left_out: number;
  ended: boolean;
  outcome?: Outcome;
  /** The refusal's code, when the stream ended in one. */
  error?: Failure;
  /** Whether the connection ended under the reply. */
  lost?: boolean;
}

export const empty: Reply = {
  text: "",
  request: "",
  thinking: false,
  started: false,
  thread: "",
  left_out: 0,
  ended: false,
};

/** apply folds one event into the reply. An ended reply moves no further. */
export function apply(r: Reply, e: Event): Reply {
  if (r.ended) {
    return r;
  }
  switch (e.kind) {
    case "request":
      return { ...r, request: e.request, started: true };
    case "thread":
      return { ...r, thread: e.thread || r.thread, left_out: e.left_out, started: true };
    case "text":
      return { ...r, text: r.text + e.text, thinking: false, started: true };
    case "progress":
      return { ...r, thinking: true, started: true };
    case "done":
      return { ...r, thinking: false, ended: true, outcome: outcomeOf(e.headers) };
    case "error": {
      const error: Failure = { code: e.code, message: e.message };
      if (e.escalation !== undefined) {
        error.escalation = e.escalation;
      }
      return { ...r, thread: r.thread || (e.thread ?? ""), thinking: false, ended: true, error };
    }
    case "lost":
      return { ...r, thinking: false, ended: true, lost: true };
  }
}

/** The refusal codes shown as *Not allowed*: the broker's, the identity's and a rule's. */
export const NOT_ALLOWED: readonly string[] = [
  "identity-required",
  "identity-invalid",
  "guard-denied",
  "not-admitted",
  "not-allowed",
  "unauthorized",
];

/** CODE_ESCALATED is a rule asking for a person's yes: the turn waits on the card in the thread. */
export const CODE_ESCALATED = guard.CODE_ESCALATED;

/** CODE_CANCELLED is the terminator of a reply the person stopped. */
export const CODE_CANCELLED = "cancelled";

/**
 * words is what the thread says of a refusal, in the console's words: *Not
 * allowed* for an identity refusal or a rule's denial, the approval for an
 * escalation, *failed* with the service's reason for the rest
 * (02-DESIGN/03-console/01-the-console.md § the words).
 */
export function words(code: string, message: string): string {
  if (NOT_ALLOWED.includes(code)) {
    return "Not allowed";
  }
  if (code === CODE_ESCALATED) {
    return "This turn needs an approval. Open Approvals, then send again.";
  }
  if (code === CODE_CANCELLED) {
    return "Stopped";
  }
  if (code === "no-service" || code === "no-responders") {
    return "Not running here";
  }
  if (code === "no-answer") {
    return "No answer";
  }
  return message === "" ? `failed: ${code}` : `failed: ${message}`;
}

/** Note is what a thread says after its last turn, until the next. */
export interface Note {
  /** How many older turns the assistant did not send. */
  leftOut?: number;
  /** A refusal, in the console's words. */
  error?: string;
  lost?: boolean;
  /** The crossing a `guard-escalated` turn waits on, drawn as the card. */
  escalation?: string;
  /** What *Approve and send* sends: the same transcript again, or — where the person's turn was never written — the turn itself. */
  retry?: SendBody;
  /** The person's text where no thread holds it: kept above the card. */
  unsent?: string;
}

/**
 * noteOf is what the thread says once a turn ended: the turns left out, the
 * refusal in the console's words, a cut connection, or the crossing it waits
 * on — with the retry, which is the resend wherever the thread holds the
 * person's turn, so that a retry never appends it twice.
 */
export function noteOf(reply: Reply, body: SendBody, thread: string, held: boolean): Note {
  const n: Note = {};
  if (reply.left_out > 0) {
    n.leftOut = reply.left_out;
  }
  if (reply.lost === true) {
    n.lost = true;
  }
  const err = reply.error;
  if (err === undefined || err.code === CODE_CANCELLED) {
    return n;
  }
  if (err.code === CODE_ESCALATED && err.escalation !== undefined) {
    n.escalation = err.escalation;
    if (body.text === "" || held) {
      n.retry = resendBody(thread, body.instance);
    } else {
      n.retry = body;
      n.unsent = body.text;
    }
    return n;
  }
  n.error = words(err.code, err.message);
  return n;
}

/** Sending is what became of an approval's resend: sent at once, or waiting for the reply in flight to end. */
export type Sending = "sent" | "queued";

/**
 * Pending is what an approval sends when it cannot be sent at once: one turn
 * runs at a time, so a resend granted while another thread's reply streams
 * waits for that reply to end, and then runs for its own thread, whichever
 * thread the person has open by then.
 */
export interface Pending {
  /** What to send now, if anything. */
  now?: SendBody;
  /** What waits, oldest first, at most one per thread. */
  queued: SendBody[];
}

/** ask is a resend granted while a reply is, or is not, in flight: sent now when nothing streams, queued behind it when something does. */
export function ask(busy: boolean, body: SendBody, queued: readonly SendBody[]): Pending {
  if (!busy) {
    return { now: body, queued: [...queued] };
  }
  return { queued: [...queued.filter((q) => q.thread !== body.thread), body] };
}

/** drain is what runs once the reply in flight ended: the oldest resend waiting, the rest still waiting. */
export function drain(queued: readonly SendBody[]): Pending {
  const [now, ...rest] = queued;
  return now === undefined ? { queued: [] } : { now, queued: rest };
}
